import { useState, useEffect } from 'react';

const TOKEN_KEY = 'lineageatlas_token';

// In production (Cloudflare Pages), API_BASE points to the Worker URL.
// In dev, it's empty (Vite proxy handles /api → localhost:8788).
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

function buildHeaders(): HeadersInit {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export function useApi<T>(url: string): ApiState<T> {
  const [state, setState] = useState<ApiState<T>>({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!url) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    let cancelled = false;

    async function fetchData() {
      setState({ data: null, loading: true, error: null });

      try {
        const res = await fetch(`${API_BASE}${url}`, { headers: buildHeaders() });
        if (res.status === 401) {
          throw new Error('Unauthorized — please log in');
        }
        if (!res.ok) {
          throw new Error(`API error: ${res.status}`);
        }
        const json = (await res.json()) as { data: T };
        if (!cancelled) {
          setState({ data: json.data, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            data: null,
            loading: false,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    }

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return state;
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<{ data: T }> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...buildHeaders(),
      ...(options.headers as Record<string, string> | undefined),
    },
  });
  if (res.status === 401) {
    throw new Error('Unauthorized — please log in');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `API error: ${res.status}`);
  }
  return res.json() as Promise<{ data: T }>;
}
